import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AlertContext } from 'src/context/AlertContext'
import { useGlobalSelectedCompany } from 'src/context/GlobalSelectedCompanyContext'
import {
  FileRepositoryModel,
  FileRepositoryListParams,
} from 'src/models/file-repository-model'
import { getCompanyId, getPermissions, getRole } from 'src/service/authService'
import {
  fetchFileRepositoryEntries,
  createDirectory,
  uploadFileRepository,
  updateFileRepository,
  deleteFileRepository,
} from 'src/service/fileRepositoryService'
import { ROUTES } from 'src/routes/Router'

export interface BreadcrumbItem {
  id: string | null // null = root
  name: string
}

export function useFileRepository() {
  // ── State ──────────────────────────────────────────────────
  const [entries, setEntries] = useState<FileRepositoryModel[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [currentParentId, setCurrentParentId] = useState<string | null>(null)
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([
    { id: null, name: 'fileRepository.breadcrumb.root' },
  ])
  const [page, setPage] = useState(1)
  const [limit] = useState(50)
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'sizeInBytes'>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [search, setSearch] = useState('')
  const { createCRUDAlert } = useContext(AlertContext)

  // ── TECNOIT company selection ─────────────────────────────
  const isTecnoit = getRole() === 'TECNOIT'
  const { isGlobalAdminSelected, selectedCompanyData } = useGlobalSelectedCompany()

  /** Resolved companyId for list/create operations (TECNOIT only) */
  const tecnoitCompanyId = useMemo(() => {
    if (!isTecnoit) return undefined
    if (!isGlobalAdminSelected() && selectedCompanyData) {
      const company = selectedCompanyData as any
      return (company.id || company.companyId) as string
    }
    return undefined
  }, [isTecnoit, selectedCompanyData])

  // ── Permissions ────────────────────────────────────────────
  const permissions = useMemo(() => JSON.parse(getPermissions()), [])
  const canCreate = permissions?.['file-repository']?.includes('file-repository:create') ?? false
  const canUpdate = permissions?.['file-repository']?.includes('file-repository:update') ?? false
  const canDelete = permissions?.['file-repository']?.includes('file-repository:delete') ?? false
  const canDownload = permissions?.['file-repository']?.includes('file-repository:download') ?? false

  // ── Reset navigation when TECNOIT switches company ─────────
  useEffect(() => {
    if (isTecnoit) {
      setCurrentParentId(null)
      setBreadcrumbs([{ id: null, name: 'fileRepository.breadcrumb.root' }])
      setPage(1)
      setSearch('')
    }
  }, [tecnoitCompanyId])

  // ── Load data ──────────────────────────────────────────────
  const loadEntries = useCallback(async () => {
    setLoading(true)
    const params: FileRepositoryListParams = {
      parentId: currentParentId,
      page,
      limit,
      sortBy,
      sortOrder,
    }
    if (search) params.search = search
    if (isTecnoit && tecnoitCompanyId) params.companyId = tecnoitCompanyId

    const result = await fetchFileRepositoryEntries(params)
    if (result) {
      setEntries(result.data)
      setTotal(result.total)
    }
    setLoading(false)
  }, [currentParentId, page, limit, sortBy, sortOrder, search, tecnoitCompanyId])

  useEffect(() => {
    loadEntries()
  }, [loadEntries])

  // ── Navigation ─────────────────────────────────────────────
  const navigateToDirectory = useCallback(
    (dir: FileRepositoryModel) => {
      setCurrentParentId(dir.id)
      setBreadcrumbs((prev) => [...prev, { id: dir.id, name: dir.name }])
      setPage(1)
      setSearch('')
    },
    []
  )

  const navigateToBreadcrumb = useCallback(
    (index: number) => {
      const target = breadcrumbs[index]
      setCurrentParentId(target.id)
      setBreadcrumbs(breadcrumbs.slice(0, index + 1))
      setPage(1)
      setSearch('')
    },
    [breadcrumbs]
  )

  // ── Actions ────────────────────────────────────────────────
  const handleCreateDirectory = async (
    name: string,
    description?: string,
    companyId?: string
  ) => {
    const res = await createDirectory({
      name,
      parentId: currentParentId,
      description,
      companyId: companyId || tecnoitCompanyId,
    })
    if (res) {
      createCRUDAlert(ROUTES.FILEREPOSITORY, name, 'created')
      loadEntries()
    }
    return res
  }

  const handleUpload = async (
    file: File,
    description?: string,
    companyId?: string
  ) => {
    const res = await uploadFileRepository(
      file,
      currentParentId,
      description,
      companyId || tecnoitCompanyId
    )
    if (res) {
      createCRUDAlert(ROUTES.FILEREPOSITORY, file.name, 'created')
      loadEntries()
    }
    return res
  }

  const handleRename = async (id: string, newName: string) => {
    const res = await updateFileRepository(id, {
      name: newName,
      companyId: tecnoitCompanyId,
    })
    if (res) {
      createCRUDAlert(ROUTES.FILEREPOSITORY, newName, 'edited')
      loadEntries()
    }
    return res
  }

  const handleMove = async (id: string, targetParentId: string | null) => {
    const res = await updateFileRepository(id, {
      parentId: targetParentId,
      companyId: tecnoitCompanyId,
    })
    if (res) {
      createCRUDAlert(ROUTES.FILEREPOSITORY, (res as FileRepositoryModel).name, 'edited')
      loadEntries()
    }
    return res
  }

  const handleDelete = async (id: string, name: string) => {
    const res = await deleteFileRepository(id, tecnoitCompanyId)
    if (res) {
      createCRUDAlert(ROUTES.FILEREPOSITORY, name, 'deleted')
      loadEntries()
    }
    return res
  }

  const handleDownload = async (entry: FileRepositoryModel) => {
    if (entry.url) {
      const link = document.createElement('a')
      link.href = entry.url
      link.download = entry.name
      link.target = '_blank'
      link.click()
    }
  }

  return {
    // Data
    entries,
    total,
    loading,
    currentParentId,
    breadcrumbs,
    page,
    limit,
    sortBy,
    sortOrder,
    search,
    // TECNOIT
    isTecnoit,
    tecnoitCompanyId,
    selectedCompanyData,
    isGlobalAdminSelected,
    // Permissions
    canCreate,
    canUpdate,
    canDelete,
    canDownload,
    // Setters
    setPage,
    setSortBy,
    setSortOrder,
    setSearch,
    // Navigation
    navigateToDirectory,
    navigateToBreadcrumb,
    // Actions
    handleCreateDirectory,
    handleUpload,
    handleRename,
    handleMove,
    handleDelete,
    handleDownload,
    refreshEntries: loadEntries,
  }
}
